import React, { useEffect, useMemo, useState } from "react";
import "../../styles/App.css";
import {
  buildFullMatrix,
  computeAhpWeights,
  formatCellValue,
  SAATY_SCALE,
  CR_THRESHOLD,
} from "../../decision/ahp.js";
import {
  DEFAULT_CRITERIA_BY_LEVEL,
  criteriaFromWeights,
} from "../../decision/criteria.js";
import {
  getActiveWeightsProfile,
  saveWeightsProfile,
  DssApiError,
} from "../../api/dss.js";

const NAME_PATTERN = /^[a-z0-9-]{3,64}$/;

function suggestName(level) {
  return `weights-lvl${level}-${Date.now()}`;
}

const WeightsPage = () => {
  const [level, setLevel] = useState(1);
  const [criteria, setCriteria] = useState(DEFAULT_CRITERIA_BY_LEVEL[1]);
  const [loadingCriteria, setLoadingCriteria] = useState(true);
  const [upperValues, setUpperValues] = useState({});
  const [name, setName] = useState(() => suggestName(1));
  const [justification, setJustification] = useState("");
  const [saving, setSaving] = useState(false);
  // Попередження, якщо активний профіль не вдалося прочитати з іншої
  // причини, ніж "профілю ще немає" (404): показано типовий набір критеріїв.
  const [criteriaWarning, setCriteriaWarning] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadCriteria = async () => {
      setLoadingCriteria(true);
      setCriteriaWarning("");
      try {
        const active = await getActiveWeightsProfile(level);
        if (!cancelled) {
          setCriteria(criteriaFromWeights(active.weights));
        }
      } catch (err) {
        if (!cancelled) {
          // 404 -- для рівня ще немає активного профілю, це штатний перший
          // запуск. Будь-яка інша помилка (мережа, 5xx, 401) -- теж типові
          // критерії, але з видимим попередженням і записом у консоль.
          const noActiveProfile = err instanceof DssApiError && err.status === 404;
          if (!noActiveProfile) {
            console.error(err);
            setCriteriaWarning(
              "Не вдалося завантажити активний профіль ваг -- показано типовий набір критеріїв."
            );
          }
          setCriteria(DEFAULT_CRITERIA_BY_LEVEL[level] || []);
        }
      } finally {
        if (!cancelled) {
          setLoadingCriteria(false);
          setUpperValues({});
          setName(suggestName(level));
        }
      }
    };

    loadCriteria();
    return () => {
      cancelled = true;
    };
  }, [level]);

  const matrix = useMemo(
    () => buildFullMatrix(criteria.length, upperValues),
    [criteria.length, upperValues]
  );

  const { cr, isConsistent } = useMemo(
    () => computeAhpWeights(matrix),
    [matrix]
  );

  const handleCellChange = (i, j, value) => {
    setUpperValues((prev) => ({ ...prev, [`${i}-${j}`]: Number(value) }));
  };

  const nameValid = NAME_PATTERN.test(name);
  const justificationValid = justification.trim().length > 0;
  const canSave =
    isConsistent && nameValid && justificationValid && !saving && criteria.length > 0;

  const handleSave = async () => {
    const comparisons = [];
    for (let i = 0; i < criteria.length; i += 1) {
      for (let j = i + 1; j < criteria.length; j += 1) {
        comparisons.push({
          a: criteria[i].code,
          b: criteria[j].code,
          value: upperValues[`${i}-${j}`] ?? 1,
        });
      }
    }

    setSaving(true);
    try {
      const profile = await saveWeightsProfile({
        name,
        level,
        comparisons,
        note: justification,
      });
      alert(
        `Профіль ваг "${profile.name}" збережено (CR = ${profile.cr.toFixed(3)}).`
      );
      setUpperValues({});
      setJustification("");
      setName(suggestName(level));
    } catch (err) {
      if (err instanceof DssApiError && err.status === 422 && err.data?.cr !== undefined) {
        alert(
          `Бекенд відхилив судження як неузгоджені (CR = ${Number(
            err.data.cr
          ).toFixed(3)}). Перегляньте матрицю.`
        );
      } else {
        alert("Не вдалося зберегти профіль ваг.");
      }
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const crBadgeStyle = {
    backgroundColor: isConsistent ? "var(--free-room)" : "var(--full-busy)",
    color: "#fff",
  };

  return (
    <div className="page-container">
      <h3 className="text-header">Матриця попарних порівнянь критеріїв (AHP)</h3>

      <div className="header-content">
        <div className="input-block text-header">
          <p className="text-header">Рівень критеріїв:</p>
          <select
            className="header-content-select"
            value={level}
            onChange={(e) => setLevel(Number(e.target.value))}
          >
            <option value={1}>Рівень 1 — черга заявників</option>
          </select>
        </div>
        <div className="input-block text-header">
          <p className="text-header">Назва профілю:</p>
          <input
            type="text"
            className="input-component-with-border"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          {!nameValid && (
            <p className="text-header" style={{ color: "var(--full-busy)" }}>
              Лише малі латинські літери, цифри та дефіс, 3-64 символи.
            </p>
          )}
        </div>
      </div>

      {criteriaWarning && (
        <p className="text-header" role="alert" style={{ color: "var(--full-busy)" }}>
          {criteriaWarning}
        </p>
      )}

      {loadingCriteria ? (
        <p className="page-container">Завантаження критеріїв...</p>
      ) : (
        <>
          <table className="table">
            <thead>
              <tr>
                <th className="table-header"> </th>
                {criteria.map((c) => (
                  <th className="table-header" key={c.code}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {criteria.map((rowCriterion, i) => (
                <tr key={rowCriterion.code}>
                  <th className="table-header">{rowCriterion.label}</th>
                  {criteria.map((colCriterion, j) => {
                    if (i === j) {
                      return (
                        <td className="table-content" key={colCriterion.code}>
                          1
                        </td>
                      );
                    }
                    if (j > i) {
                      const value = upperValues[`${i}-${j}`] ?? 1;
                      return (
                        <td className="table-content" key={colCriterion.code}>
                          <select
                            className="ahp-matrix-cell-input"
                            value={value}
                            onChange={(e) =>
                              handleCellChange(i, j, e.target.value)
                            }
                          >
                            {SAATY_SCALE.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </td>
                      );
                    }
                    return (
                      <td
                        className="table-content ahp-matrix-cell-readonly"
                        key={colCriterion.code}
                      >
                        {formatCellValue(matrix[i][j])}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>

          <p className="text-header">
            Відношення узгодженості:{" "}
            <span className="badge" style={crBadgeStyle}>
              CR = {cr.toFixed(3)} {isConsistent ? "≤" : ">"} {CR_THRESHOLD}
            </span>
          </p>

          <div>
            <p className="text-header">Обґрунтування суджень: *</p>
            <textarea
              className="input-component-with-border"
              style={{ width: "97%", minHeight: "80px" }}
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              placeholder="Поясніть, чому обрано саме такі співвідношення важливості критеріїв..."
            />
            {!justificationValid && (
              <p className="text-header" style={{ color: "var(--full-busy)" }}>
                Поле обов'язкове.
              </p>
            )}
          </div>

          <button
            className="restore-button"
            style={{ width: "50%", marginLeft: 0, marginTop: "12px" }}
            disabled={!canSave}
            onClick={handleSave}
          >
            {saving ? "Збереження..." : "Зберегти профіль ваг"}
          </button>
        </>
      )}
    </div>
  );
};

export default WeightsPage;
